// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
/**
* Represents a Nano network node. It primarily consists of a URL which will
* accept RPC calls, and an optional API key header construction can be passed if
* required by the node. Once instantiated, the Rpc object can be used to call
* any action supported by the Nano protocol. The URL protocol must be HTTPS; any
* other value will be changed automatically.
*/
export class Rpc {
    #u;
    #n;
    constructor(url, apiKeyName) {
        this.#u = new URL(url);
        this.#u.protocol = 'https:';
        this.#n = apiKeyName;
    }
    /**
     *
     * @param {string} action - Nano protocol RPC call to execute
     * @param {object} [data] - JSON to send to the node as defined by the action
     * @returns JSON-formatted RPC results from the node
     */
    async call(action, data) {
        this.#validate(action);
        const headers = {};
        headers['Content-Type'] = 'application/json';
        if (this.#n && process.env.LIBNEMO_RPC_API_KEY) {
            headers[this.#n] = process.env.LIBNEMO_RPC_API_KEY;
        }
        data ??= {};
        data.action = action.toLowerCase();
        const body = JSON.stringify(data)
            .replaceAll('/', '\\u002f')
            .replaceAll('<', '\\u003c')
            .replaceAll('>', '\\u003d')
            .replaceAll('\\', '\\u005c');
        const req = new Request(this.#u, {
            method: 'POST',
            headers,
            body
        });
        try {
            const res = await fetch(req);
            return await res.json();
        }
        catch (err) {
            console.error(err);
            return JSON.stringify(err);
        }
    }
    #validate(action) {
        if (!action) {
            throw new ReferenceError('Action is required for RPCs');
        }
        if (typeof action !== 'string') {
            throw new TypeError('RPC action must be a string');
        }
        if (!/^[A-Za-z]+(_[A-Za-z]+)*$/.test(action)) {
            throw new TypeError('RPC action contains invalid characters');
        }
    }
}
